import React, { useState } from "react";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
} from "@/components/ui/alert-dialog";

// Each level maps to a piece range (batch = 40 pieces).
export const PALETA_LEVELS = [
  { value: 0, label: "0 piezas", range: "0", src: "/paletas/000.png", color: "#9E9E9E" },
  { value: 25, label: "1 - 10 piezas", range: "1 - 10", src: "/paletas/025.png", color: "#C64545" },
  { value: 50, label: "11 - 20 piezas", range: "11 - 20", src: "/paletas/050.png", color: "#F0A030" },
  { value: 75, label: "21 - 30 piezas", range: "21 - 30", src: "/paletas/075.png", color: "#F5D93A" },
  { value: 100, label: "31 - 40 piezas", range: "31 - 40", src: "/paletas/100.png", color: "#9EF07A" },
];

export function paletaImg(level) {
  const found = PALETA_LEVELS.find((l) => l.value === level);
  return (found || PALETA_LEVELS[PALETA_LEVELS.length - 1]).src;
}

export function paletaRange(level) {
  const found = PALETA_LEVELS.find((l) => l.value === level);
  return (found || PALETA_LEVELS[PALETA_LEVELS.length - 1]).range;
}

export default function PaletaCard({ item, onChangeLevel, disabled = false }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const level = typeof item.stock_level === "number" ? item.stock_level : 100;
  const isLow = level <= 50;

  const pick = async (newLevel) => {
    if (newLevel === level) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      await onChangeLevel(item, newLevel);
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <motion.button
        type="button"
        onClick={() => !disabled && setOpen(true)}
        whileTap={{ scale: 0.96 }}
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        transition={{ type: "spring", stiffness: 400, damping: 22 }}
        data-testid={`pl-card-${item.id}`}
        data-level={level}
        disabled={disabled}
        className={`group relative w-full flex flex-col rounded-2xl border bg-white text-left shadow-sm focus:outline-none focus-visible:ring-4 focus-visible:ring-[#F7C8E0] ${
          isLow ? "border-[#FF7878] ring-2 ring-[#FF7878]/40" : "border-[#EADBCE] hover:shadow-md"
        } ${disabled ? "opacity-70 cursor-not-allowed" : "cursor-pointer"}`}
      >
        <div className="relative aspect-square w-full p-2 bg-[#FCF9F2] rounded-t-2xl">
          <img
            src={paletaImg(level)}
            alt={`${item.name} — ${paletaRange(level)} piezas`}
            className="h-full w-full object-contain transition-transform duration-300 group-hover:scale-105"
          />
          <div className="absolute top-2 right-2 px-2.5 py-0.5 rounded-full bg-white/95 text-[11px] font-extrabold text-[#4A3B32] shadow-sm border border-[#EADBCE]">
            {paletaRange(level)}
          </div>
        </div>

        <div className="px-3 py-2 border-t border-[#EADBCE]">
          <span className="block font-display font-bold leading-tight text-[#4A3B32] line-clamp-2 text-sm sm:text-base">
            {item.name}
          </span>
          {item.marked_by && (
            <span className="text-[10px] text-[#8A796F] truncate mt-0.5 block">
              por {item.marked_by.name}
            </span>
          )}
        </div>
      </motion.button>

      <AlertDialog open={open} onOpenChange={(v) => !saving && setOpen(v)}>
        <AlertDialogContent
          className="rounded-3xl border-[#EADBCE] bg-white max-w-md p-6"
          data-testid={`pl-dialog-${item.id}`}
        >
          <AlertDialogHeader className="text-left">
            <AlertDialogTitle className="font-display text-2xl text-[#4A3B32]">
              {item.name}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#8A796F]">
              ¿Cuántas paletas quedan? (Lote = 40)
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="mt-4 grid grid-cols-5 gap-2">
            {PALETA_LEVELS.map((l) => {
              const active = level === l.value;
              return (
                <button
                  key={l.value}
                  type="button"
                  disabled={saving}
                  onClick={() => pick(l.value)}
                  data-testid={`pl-level-${l.value}`}
                  className={`flex flex-col items-center gap-1 rounded-xl border-2 p-2 transition-all ${
                    active
                      ? "border-[#FF7878] bg-[#FFF0F0] ring-2 ring-[#FF7878]/30 scale-105"
                      : "border-[#EADBCE] hover:border-[#FF7878]/60 hover:bg-[#FCF9F2] active:scale-95"
                  } disabled:opacity-50`}
                >
                  <img src={l.src} alt={l.label} className="w-full aspect-square object-contain" />
                  <div className="text-center leading-tight">
                    <div className="text-[10px] font-extrabold text-[#4A3B32]">{l.range}</div>
                    <div className="text-[9px] text-[#8A796F] leading-none">piezas</div>
                  </div>
                </button>
              );
            })}
          </div>
          {saving && (
            <div className="flex items-center justify-center mt-3 text-[#8A796F] text-sm gap-2">
              <Loader2 className="w-4 h-4 animate-spin" /> Guardando...
            </div>
          )}
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
